import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputAdornment,
  ListItem,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import Loader from 'src/components/Loader';
import { useKeydown } from 'src/hooks/useKeydown';
import { ReactComponent as ArrowDropDownIcon } from 'src/icons/arrowDropDown.svg';
import { ReactComponent as CheckboxOffIcon } from 'src/icons/checkboxOff.svg';
import { ReactComponent as CheckboxOnIcon } from 'src/icons/checkboxOn.svg';
import { ReactComponent as ViewWeekIcon } from 'src/icons/viewWeek.svg';
import palette from 'src/theme/palette';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { getIconForMetric } from 'src/utils';
import {
  ConnectorType,
  Metric,
  Split,
  MetricsByConnectorType,
} from 'src/utils/gql/types';

import {
  facebookDeviceSplits,
  facebookRegionSplitKey,
  facebookRegionSplits,
  incompatibleRegionSplittingMetricKeys,
  SPLITS_MAX,
} from '../constants';
import {
  checkboxStyles,
  CustomPopper,
  DropdownButton,
  splitNameStyles,
  splitsInputStyles,
  SplitsInputWrapper,
  SplitsList,
  SplitsListItem,
  SplitsListItemButton,
} from '../styled';
import { DashboardForm, InputName, SplitFilterProps } from '../types';
import { sortArrayBySecondArray } from '../utils';
import {
  isDatabaseFieldJoinCompatible,
  shouldShowDatabaseField,
} from '../../DatabaseConnector/databaseJoinCompatibility';

import { Badge } from './Badge';

const checkIfSelectedSplitsHasOppositeGroupSplit = (
  currentSplitKey: string,
  selectedSplits: Split[],
  checkedGroup: Set<string>,
  oppositeGroup: Set<string>,
): boolean =>
  checkedGroup.has(currentSplitKey) &&
  selectedSplits.some(
    ({ connectorType, key }) =>
      connectorType === ConnectorType.FACEBOOK && oppositeGroup.has(key),
  );

const checkDisableConnectorSplitOption = (
  selectedSplits: Split[],
  currentSplit: Split,
  selectedMetrics: Metric[],
): boolean => {
  return (
    currentSplit.connectorType === ConnectorType.FACEBOOK &&
    selectedSplits.every(({ key }) => key !== currentSplit.key) &&
    (checkIfSelectedSplitsHasOppositeGroupSplit(
      currentSplit.key,
      selectedSplits,
      facebookDeviceSplits,
      facebookRegionSplits,
    ) ||
      checkIfSelectedSplitsHasOppositeGroupSplit(
        currentSplit.key,
        selectedSplits,
        facebookRegionSplits,
        facebookDeviceSplits,
      ) ||
      (currentSplit.key === facebookRegionSplitKey &&
        selectedMetrics.some(({ key }) =>
          incompatibleRegionSplittingMetricKeys.has(key),
        )))
  );
};
// end of temporary code block

export const SplitsFilter = ({ splits }: SplitFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { databaseJoinEdges = [], databaseMainTables = [] } = useConnectors();

  const { control, setValue, watch, getValues } =
    useFormContext<DashboardForm>();

  const [searchValue, setSearchValue] = useState<string>('');
  const [ulScrollTop, setUlScrollTop] = React.useState<{
    list: HTMLUListElement;
    position: number;
  } | null>(null);

  const checkDisableOption = (field: Split) => {
    const selectedSplits = getValues(InputName.splits);
    const selectedMetrics = getValues(InputName.metrics);
    const isAlreadySelected = selectedSplits.some(({ id }) => id === field.id);

    const dbSplitKeys = selectedSplits
      .filter(({ connectorType }) => connectorType === ConnectorType.DATABASE)
      .map(({ key }) => key);
    const dbMetricKeys = selectedMetrics
      .filter(({ type }) => type === ConnectorType.DATABASE)
      .map(({ key }) => key);

    const isDatabaseJoinIncompatible =
      field.connectorType === ConnectorType.DATABASE &&
      dbMetricKeys.length > 0 &&
      !isDatabaseFieldJoinCompatible(
        field.key,
        dbSplitKeys,
        dbMetricKeys,
        databaseJoinEdges,
        databaseMainTables,
      );

    return (
      (selectedSplits.length >= SPLITS_MAX && !isAlreadySelected) ||
      (!isAlreadySelected &&
        checkDisableConnectorSplitOption(
          selectedSplits,
          field,
          selectedMetrics,
        )) ||
      (!isAlreadySelected && isDatabaseJoinIncompatible)
    );
  };

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    value: Split[],
    field: Split,
  ) => {
    if (checkDisableOption(field)) {
      return;
    }

    const list = e.currentTarget.closest('ul');

    const scrollPosition = list?.scrollTop;

    if (value.some((item) => item.id === field.id)) {
      setValue(
        InputName.splits,
        value.filter((item) => item.id !== field.id),
      );
    } else {
      setValue(InputName.splits, [...value, field]);
    }

    if (list && scrollPosition)
      setUlScrollTop({ list, position: list.scrollTop });
  };

  useLayoutEffect(() => {
    if (ulScrollTop?.list) {
      ulScrollTop.list.scrollTop = ulScrollTop.position; // Восстановить позицию
      setUlScrollTop(null); // Сбросить состояние
    }
  }, [ulScrollTop]);

  const filteredSplits = useMemo(
    () => {
      const selectedMetrics = watch(InputName.metrics) || [];
      const dbMetricKeys = selectedMetrics
        .filter(({ type }) => type === ConnectorType.DATABASE)
        .map(({ key }) => key);
      const selectedSplitKeys = (watch(InputName.splits) || [])
        .filter(({ connectorType }) => connectorType === ConnectorType.DATABASE)
        .map(({ key }) => key);

      return splits.filter(({ label, connectorType, key }) => {
        if (!label.toLowerCase().includes(searchValue.toLowerCase())) {
          return false;
        }

        if (connectorType !== ConnectorType.DATABASE || !dbMetricKeys.length) {
          return true;
        }

        return shouldShowDatabaseField(
          key,
          selectedSplitKeys,
          dbMetricKeys,
          databaseJoinEdges,
          databaseMainTables,
          'split',
        );
      });
    },
    [
      splits,
      searchValue,
      watch(InputName.metrics),
      watch(InputName.splits),
      databaseJoinEdges,
      databaseMainTables,
    ],
  );

  // Ordina gli split: selezionati → Kipify non selezionati → altri
  const sortedSplits = useMemo(() => {
    const selectedSplits = watch(InputName.splits) || [];
    const selectedIds = new Set(selectedSplits.map((s) => s.id));

    // Separa in: selezionati, Kipify non selezionati, altri non selezionati
    const selected: Split[] = [];
    const kipifyNotSelected: Split[] = [];
    const othersNotSelected: Split[] = [];

    filteredSplits.forEach((split) => {
      if (selectedIds.has(split.id)) {
        selected.push(split);
      } else if (
        split.connectorType === ConnectorType.KIPIFY ||
        (split.connectorType as any) === MetricsByConnectorType.KIPIFY
      ) {
        kipifyNotSelected.push(split);
      } else {
        othersNotSelected.push(split);
      }
    });

    // Ordina: selezionati (mantiene ordine originale) → Kipify → altri
    return [...selected, ...kipifyNotSelected, ...othersNotSelected];
  }, [filteredSplits, watch(InputName.splits)]);

  const isLoader = !filteredSplits.length;

  const handleClickAway = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.stopPropagation();

    if (anchorEl) setAnchorEl(null);

    if (searchValue) setSearchValue('');
  };

  useKeydown('Escape', handleClickAway);

  return (
    <ClickAwayListener onClickAway={() => handleClickAway()}>
      <Box>
        <Box onClick={(e) => setAnchorEl(e.currentTarget)}>
          {anchorEl ? (
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              InputProps={{
                sx: splitsInputStyles,
                startAdornment: (
                  <InputAdornment position="start">
                    <Badge badgeContent={watch(InputName.splits).length} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <DropdownButton onClick={(e) => handleClickAway(e)}>
                      <ArrowDropDownIcon />
                    </DropdownButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <SplitsInputWrapper>
              <Box display="flex" gap="5px" alignItems="center">
                <ViewWeekIcon />
                <Typography fontSize="16px" lineHeight="21px" fontWeight={600}>
                  Splits
                </Typography>
                <Badge badgeContent={watch(InputName.splits).length} />
              </Box>

              <ArrowDropDownIcon
                color={palette.grey[800]}
                height={24}
                width={24}
              />
            </SplitsInputWrapper>
          )}
        </Box>
        <CustomPopper
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: 1100 }}
        >
          <Controller
            name={InputName.splits}
            control={control}
            render={({ field: { value } }) => (
              <SplitsList sx={{ height: isLoader ? '60px' : undefined }}>
                {sortedSplits.map((split) => {
                  return (
                    <SplitsListItem key={split.id}>
                      <SplitsListItemButton
                        onClick={(e) => handleClick(e, value, split)}
                        disableRipple
                      >
                        <Checkbox
                          sx={checkboxStyles}
                          icon={<CheckboxOffIcon height={24} width={24} />}
                          checkedIcon={
                            <CheckboxOnIcon height={24} width={24} />
                          }
                          checked={value.some((item) => item.id === split.id)}
                          disabled={checkDisableOption(split)}
                        />
                        <Box display="flex" gap="5px" mr="5px">
                          {getIconForMetric(split.connectorType)}
                        </Box>
                        <Typography
                          sx={splitNameStyles(checkDisableOption(split))}
                        >
                          {split.label}
                        </Typography>
                      </SplitsListItemButton>
                    </SplitsListItem>
                  );
                })}
                {isLoader && <Loader />}
                {!filteredSplits?.length && (
                  <ListItem sx={{ padding: 0 }}>
                    <SplitsListItemButton disableRipple>
                      <Typography>No options</Typography>
                    </SplitsListItemButton>
                  </ListItem>
                )}
              </SplitsList>
            )}
          />
        </CustomPopper>
      </Box>
    </ClickAwayListener>
  );
};
